import React, { useState, useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

interface DepartmentDialogsProps {
  showAddDialog: boolean;
  showEditDialog: boolean;
  parentDepartment?: any;
  editingDepartment?: any;
  onCloseAdd: () => void;
  onCloseEdit: () => void;
  onAddDepartment: (department: {
    name: string;
    code: string;
    description: string;
    parentId: string;
  }) => Promise<boolean>;
  onEditDepartment: (department: { id: string; name: string; code: string; description: string }) => Promise<boolean>;
}

const DepartmentDialogs: React.FC<DepartmentDialogsProps> = ({
  showAddDialog,
  showEditDialog,
  parentDepartment,
  editingDepartment,
  onCloseAdd,
  onCloseEdit,
  onAddDepartment,
  onEditDepartment,
}) => {
  const { t } = useTranslation();
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptDescription, setNewDeptDescription] = useState('');
  const [editDeptName, setEditDeptName] = useState('');
  const [editDeptCode, setEditDeptCode] = useState('');
  const [editDeptDescription, setEditDeptDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // 初始化编辑部门的数据
  useEffect(() => {
    if (editingDepartment) {
      setEditDeptName(editingDepartment.name || '');
      setEditDeptCode(editingDepartment.code || '');
      setEditDeptDescription(editingDepartment.description || '');
    }
  }, [editingDepartment]);

  // 重置新增表单
  const resetAddForm = () => {
    setNewDeptName('');
    setNewDeptDescription('');
  };

  // 处理添加子部门
  const handleAddSubDepartment = async () => {
    if (!newDeptName.trim()) {
      notifications.show({
        message: t('adminConsole.newConsole.department.nameRequired'),
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      // 生成简短的部门代码（限制在10个字符以内）
      const timestamp = Date.now().toString().slice(-6); // 取时间戳后6位
      const autoCode = `D${timestamp}`;

      // 获取父部门ID
      // 如果是根节点（id为'root'），则parentId为-1（表示创建一级部门）
      let parentDeptId = '';
      if (parentDepartment?.id === 'root') {
        // 根节点下创建一级部门，parentId为-1
        parentDeptId = '-1';
      } else {
        // 使用父部门的ID
        parentDeptId = parentDepartment?.originalData?.id || parentDepartment?.id || '';
      }

      const success = await onAddDepartment({
        name: newDeptName.trim(),
        code: autoCode,
        description: newDeptDescription.trim(),
        parentId: parentDeptId,
      });

      if (success) {
        resetAddForm();
        onCloseAdd();
        // 对话框关闭后显示成功通知
        setTimeout(() => {
          notifications.show({
            message: t('adminConsole.newConsole.department.createSuccess'),
            color: 'green',
          });
        }, 300);
      }
    } catch (error) {
      console.error('Failed to create department:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑部门
  const handleEditDepartment = async () => {
    if (!editDeptName.trim()) {
      notifications.show({
        message: t('adminConsole.newConsole.department.nameRequired'),
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const success = await onEditDepartment({
        id: editingDepartment?.id || '',
        name: editDeptName.trim(),
        code: editDeptCode.trim(),
        description: editDeptDescription.trim(),
      });

      if (success) {
        onCloseEdit();
        // 对话框关闭后显示成功通知
        setTimeout(() => {
          notifications.show({
            message: t('adminConsole.newConsole.department.updateSuccess'),
            color: 'green',
          });
        }, 300);
      }
    } catch (error) {
      notifications.show({
        message: t('adminConsole.newConsole.department.updateError'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* 添加子部门对话框 */}
      <Modal
        opened={showAddDialog}
        onClose={() => {
          resetAddForm();
          onCloseAdd();
        }}
        title={t('adminConsole.newConsole.department.createTitle')}
        size="md"
      >
        <Stack>
          {parentDepartment && (
            <div>
              <Text size="sm" c="dimmed">
                {t('adminConsole.newConsole.department.parentDepartment')}
              </Text>
              <Text fw={500}>{parentDepartment.name}</Text>
            </div>
          )}

          <TextInput
            label={t('adminConsole.newConsole.department.name')}
            placeholder={t('adminConsole.newConsole.department.namePlaceholder')}
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.currentTarget.value)}
            maxLength={50}
            minLength={1}
            required
          />

          <Textarea
            label={t('adminConsole.newConsole.department.description')}
            placeholder={t('adminConsole.newConsole.department.descriptionPlaceholder')}
            value={newDeptDescription}
            onChange={(e) => setNewDeptDescription(e.currentTarget.value)}
            rows={3}
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                resetAddForm();
                onCloseAdd();
              }}
              disabled={loading}
            >
              {t('adminConsole.newConsole.department.cancel')}
            </Button>
            <Button onClick={handleAddSubDepartment} loading={loading}>
              {t('adminConsole.newConsole.department.create')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 编辑部门对话框 */}
      <Modal
        opened={showEditDialog}
        onClose={onCloseEdit}
        title={t('adminConsole.newConsole.department.editTitle')}
        size="md"
      >
        <Stack>
          <TextInput
            label={t('adminConsole.newConsole.department.name')}
            placeholder={t('adminConsole.newConsole.department.namePlaceholder')}
            value={editDeptName}
            onChange={(e) => setEditDeptName(e.currentTarget.value)}
            required
          />

          <Textarea
            label={t('adminConsole.newConsole.department.description')}
            placeholder={t('adminConsole.newConsole.department.descriptionPlaceholder')}
            value={editDeptDescription}
            onChange={(e) => setEditDeptDescription(e.currentTarget.value)}
            rows={3}
          />

          <Group justify="flex-end">
            <Button variant="outline" onClick={onCloseEdit} disabled={loading}>
              {t('adminConsole.newConsole.department.cancel')}
            </Button>
            <Button onClick={handleEditDepartment} loading={loading}>
              {t('adminConsole.newConsole.department.update')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default DepartmentDialogs;
